import { Injectable, signal, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { Router, NavigationStart } from '@angular/router';
import { ShoppingCartItem } from './shopping-cart.types';
import { SHOPPING_CART_DRAWER_WRAPPER, ShoppingCartDrawerWrapper } from './shopping-cart-drawer.types';

@Injectable({
  providedIn: 'root',
})
export class CIPXShoppingCartService {
  private _items = signal<ShoppingCartItem<any>[]>([]);
  private _drawerWrapper = inject(SHOPPING_CART_DRAWER_WRAPPER, { optional: true });
  private _router = inject(Router);

  // Event emitters for communication with other components
  private _itemRemoved = new Subject<ShoppingCartItem<any>>();
  private _itemAdded = new Subject<ShoppingCartItem<any>>();
  private _cartCleared = new Subject<void>();

  constructor() {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeDrawer();
      }
    });
  }

  // Observable streams for external components to subscribe to
  get itemRemoved$() {
    return this._itemRemoved.asObservable();
  }

  get itemAdded$() {
    return this._itemAdded.asObservable();
  }

  get cartCleared$() {
    return this._cartCleared.asObservable();
  }

  get items() {
    return this._items;
  }

  addItem(item: ShoppingCartItem<any>) {
    this._items.update((items) => {
      // Check if item with same ID already exists
      const existingItemIndex = items.findIndex(existingItem => existingItem.id === item.id);
      if (existingItemIndex !== -1) {
        // Replace existing item instead of adding duplicate
        const updatedItems = [...items];
        updatedItems[existingItemIndex] = item;
        return updatedItems;
      }
      // Add new item if it doesn't exist
      return [...items, item];
    });

    // Emit event for external components
    this._itemAdded.next(item);
  }

  removeItem(item: ShoppingCartItem<any>) {
    this._items.update((items) => items.filter((i) => i.id !== item.id));

    // Emit event for external components
    this._itemRemoved.next(item);
  }

  clear() {
    this._items.set([]);

    // Emit event for external components
    this._cartCleared.next();
  }

  /**
   * Opens the shopping cart drawer if a drawer wrapper is provided
   */
  openDrawer() {
    if (this._drawerWrapper) {
      return this._drawerWrapper.openDrawer({
        items: this._items()
      });
    } else {
      console.warn('No shopping cart drawer wrapper provided. Add SHOPPING_CART_DRAWER_WRAPPER to your app providers.');
    }
  }

  /**
   * Closes the shopping cart drawer if available
   */
  closeDrawer() {
    if (this._drawerWrapper?.closeDrawer) {
      this._drawerWrapper.closeDrawer();
    }
  }

  /**
   * Clean up resources (call this when the service is no longer needed)
   */
  ngOnDestroy() {
    this._itemRemoved.complete();
    this._itemAdded.complete();
    this._cartCleared.complete();
  }

}
